import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'

import { dateFnsLocale } from '@/utils/dateLocale'
import { Button } from '@/components/ui/Button'
import { addLeadComment, listLeadComments, requestLeadReminder } from '@/api/inspection-leads'
import { leadCardCls } from '@/components/inspections/leadDetail'
import type { InspectionLeadComment } from '@/types/inspection-lead'

/**
 * Карточка «Комментарии диспетчера» — append-only лента (новые сверху) +
 * поле ввода. Комментарии нельзя редактировать/удалять (RLS). Автор
 * проставляется БД из auth.uid(). Своя query + realtime через invalidate
 * (таблица inspection_lead_comments в publication).
 */
export function LeadDispatcherComments({ leadId }: { leadId: string }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [body, setBody] = useState('')
  const [reminderBusy, setReminderBusy] = useState(false)

  const commentsQ = useQuery({
    queryKey: ['inspection-lead', leadId, 'comments'],
    queryFn: () => listLeadComments(leadId),
    enabled: !!leadId,
  })

  const addMut = useMutation({
    mutationFn: () => addLeadComment(leadId, body),
    onSuccess: async () => {
      const submitted = body.trim()
      setBody('')
      await qc.invalidateQueries({ queryKey: ['inspection-lead', leadId, 'comments'] })
      // Команда-напоминание `@avs …` → edge lead-reminder (он допишет эхо в ленту).
      if (/^@avs\b/i.test(submitted)) {
        const command = submitted.replace(/^@avs\b\s*/i, '').trim()
        if (command) {
          setReminderBusy(true)
          try {
            await requestLeadReminder(leadId, command)
          } catch {
            // edge сам пишет эхо (в т.ч. с ошибкой); если совсем не дошли — молча
          } finally {
            setReminderBusy(false)
            await qc.invalidateQueries({ queryKey: ['inspection-lead', leadId, 'comments'] })
          }
        }
      }
    },
  })

  const comments = commentsQ.data ?? []
  const canSubmit = body.trim() !== '' && !addMut.isPending

  return (
    <section className={leadCardCls}>
      <h2 className="stryzh-heading-card mb-4">{t('leads.detail.secComments')}</h2>

      <div className="flex flex-col gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder={t('leads.detail.commentPlaceholder')}
          className="stryzh-input-field !py-2 resize-y"
          onKeyDown={(e) => {
            // Ctrl/Cmd+Enter — отправить.
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSubmit) {
              e.preventDefault()
              addMut.mutate()
            }
          }}
        />
        <div className="flex items-center justify-end gap-2">
          <Button type="button" onClick={() => addMut.mutate()} disabled={!canSubmit}>
            {addMut.isPending ? t('leads.detail.adding') : t('leads.detail.add')}
          </Button>
        </div>
        {addMut.isError ? (
          <div className="text-xs text-[color:var(--stryzh-color-accent-orange)]">
            {t('leads.detail.addFailed', { msg: (addMut.error as Error).message })}
          </div>
        ) : null}
        {reminderBusy ? (
          <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.detail.reminderBusy')}
          </div>
        ) : (
          <div className="text-[11px] leading-snug text-[color:var(--stryzh-color-text-secondary)]">
            💡 <span className="font-[family-name:var(--stryzh-font-family-mono)]">@avs напомни …</span>{' '}
            {t('leads.detail.reminderHint1')}{' '}
            <span className="font-[family-name:var(--stryzh-font-family-mono)]">@avs отмени</span> {t('leads.detail.reminderHint2')}
          </div>
        )}
      </div>

      <div className="mt-4">
        {commentsQ.isLoading ? (
          <div className="stryzh-text-muted">{t('leads.detail.loading')}</div>
        ) : comments.length === 0 ? (
          <p className="stryzh-text-muted">{t('leads.detail.commentsEmpty')}</p>
        ) : (
          <ol className="space-y-2 text-sm">
            {comments.map((c) => (
              <CommentItem key={c.id} comment={c} />
            ))}
          </ol>
        )}
      </div>
    </section>
  )
}

function CommentItem({ comment }: { comment: InspectionLeadComment }) {
  const { t } = useTranslation()
  const isSystem = comment.kind === 'system'
  return (
    <li
      className={`rounded border px-3 py-2 ${
        isSystem
          ? 'border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)]'
          : 'border-[color:var(--stryzh-color-border-subtle)] bg-[color:var(--stryzh-color-void)]'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-[color:var(--stryzh-color-text-primary)]">
          {isSystem ? (
            <>
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--stryzh-color-accent-orange)]" />
              Carlink AI
            </>
          ) : (
            comment.author_name || t('leads.detail.commentTransferred')
          )}
        </span>
        <span className="font-[family-name:var(--stryzh-font-family-mono)] text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
          {format(new Date(comment.created_at), 'd MMM HH:mm', { locale: dateFnsLocale() })}
        </span>
      </div>
      <div className="mt-1 whitespace-pre-wrap break-words text-[color:var(--stryzh-color-text-primary)]">
        {comment.body}
      </div>
    </li>
  )
}
